import { useState, useEffect, useCallback, useRef } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { MessageCircle, Loader2 } from 'lucide-react';
import FieldHelp from '@/components/help/FieldHelp';

const FB_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

function loadFacebookSdk(appId) {
  return new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }
    window.fbAsyncInit = function () {
      window.FB.init({ appId, cookie: true, xfbml: true, version: 'v21.0' });
      resolve(window.FB);
    };
    const existing = document.getElementById('facebook-jssdk');
    if (existing) {
      existing.addEventListener('load', () => resolve(window.FB));
      return;
    }
    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = FB_SDK_URL;
    script.async = true;
    script.defer = true;
    script.onerror = () => reject(new Error('No se pudo cargar el SDK de Meta'));
    document.body.appendChild(script);
  });
}

/**
 * Meta WhatsApp Embedded Signup — un clic (Tech Provider).
 * S104: espera sessionInfo (WABA/phone) antes de canjear el code (~30s TTL).
 */
export default function MetaWhatsappEmbeddedSignup({ onConnected, branchId = '', displayName = '', compact = false, configUrl = '/tenant/integrations/meta-whatsapp/embedded-signup/config', completeUrl = '/tenant/integrations/meta-whatsapp/embedded-signup/complete', avisoPago = '' }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const sessionRef = useRef(null);

  const fetchConfig = useCallback(async () => {
    try {
      const { data } = await api.get(configUrl);
      setConfig(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [configUrl]);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.origin !== 'https://www.facebook.com' && event.origin !== 'https://web.facebook.com') return;
      try {
        const data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
        if (data?.type !== 'WA_EMBEDDED_SIGNUP') return;
        const ev = data.event || data.data?.event;
        // FINISH* trae assets; CANCEL se ignora aquí (FB.login callback maneja)
        if (ev && String(ev).startsWith('CANCEL')) return;
        const payload = data.data || data;
        sessionRef.current = {
          phone_number_id: payload.phone_number_id || payload.phoneNumberId,
          waba_id: payload.waba_id || payload.wabaId,
          business_id: payload.business_id || payload.businessId,
          event: ev,
        };
      } catch {
        // ignore
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const waitForSession = (ms = 2500) => new Promise((resolve) => {
    const start = Date.now();
    const tick = () => {
      const s = sessionRef.current;
      if (s?.phone_number_id || s?.waba_id) {
        resolve(s);
        return;
      }
      if (Date.now() - start >= ms) {
        resolve(s || {});
        return;
      }
      setTimeout(tick, 100);
    };
    tick();
  });

  const finishSignup = async (code) => {
    setConnecting(true);
    try {
      const sessionInfo = await waitForSession(2500);
      const { data } = await api.post(completeUrl, {
        code,
        phone_number_id: sessionInfo?.phone_number_id,
        waba_id: sessionInfo?.waba_id,
        business_id: sessionInfo?.business_id || sessionInfo?.waba_id,
        display_name: displayName || undefined,
        branch_id: branchId || undefined,
      });
      const msg = data.tenant_message || data.onboarding?.tenant_message || 'WhatsApp conectado con Meta';
      toast.success(msg);
      if (data.onboarding && !data.onboarding.webhooks_subscribed) {
        toast.message('Conectado, pero los mensajes entrantes pueden fallar. Pulsa Verificar en Conexión.');
      }
      onConnected?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setConnecting(false);
    }
  };

  const handleEmbeddedSignup = async () => {
    if (!config?.configured) {
      toast.error('El administrador de la plataforma aún no configuró la app de Meta.');
      return;
    }
    setConnecting(true);
    sessionRef.current = null;
    try {
      await loadFacebookSdk(config.app_id);
      // ES: config_id del App Dashboard. sessionInfoVersion 3 (compatible).
      // Migración de config a v4 se hace en Meta Dashboard (no rompe este botón).
      window.FB.login((response) => {
        if (response.authResponse?.code) {
          finishSignup(response.authResponse.code);
        } else {
          setConnecting(false);
          if (response.status !== 'unknown') {
            toast.error('Meta canceló o no completó el registro. Intenta de nuevo.');
          }
        }
      }, {
        config_id: config.config_id,
        response_type: 'code',
        override_default_response_type: true,
        extras: {
          setup: {},
          featureType: '',
          sessionInfoVersion: config.session_info_version || '3',
        },
      });
    } catch (e) {
      setConnecting(false);
      toast.error(e.message || 'Error al iniciar registro con Meta');
    }
  };

  if (loading) {
    return compact ? null : (
      <div className="flex items-center gap-2 text-xs text-[#94A3B8] py-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando opción de Meta...
      </div>
    );
  }

  if (!config?.configured) {
    return compact ? null : (
      <FieldHelp
        label="Conexión rápida con Meta"
        help="Activa META_WHATSAPP_APP_ID, META_WHATSAPP_APP_SECRET y META_WHATSAPP_EMBEDDED_CONFIG_ID en el servidor. El config_id sale del Embedded Signup del App Dashboard (preferible config v4 en Meta)."
      >
        <p className="text-xs text-[#94A3B8]">Registro embebido no disponible — usa Gupshup/Twilio u opción avanzada.</p>
      </FieldHelp>
    );
  }

  return (
    <div
      className={compact ? '' : 'rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] p-4 space-y-2'}
      data-testid="meta-embedded-signup"
    >
      {!compact && (
        <FieldHelp
          label="Conectar con Meta en un clic"
          help="Ventana oficial de Meta (Tech Provider). El negocio inicia sesión, elige el número y vuelve. Nosotros canjeamos el código, suscribimos el WABA a webhooks y dejamos listo el camino a plantillas. El pago de conversaciones, si Meta lo pide, lo pone el negocio en Business Manager."
        >
          <p className="text-xs text-[#64748B] mb-2">
            Recomendado — sin copiar tokens. Si ya usas Gupshup o Twilio, no es obligatorio.
          </p>
          {avisoPago && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-2">{avisoPago}</p>}
        </FieldHelp>
      )}
      <Button
        type="button"
        onClick={handleEmbeddedSignup}
        disabled={connecting}
        className={compact ? 'w-full bg-[#1877F2] hover:bg-[#166FE5] text-white' : 'bg-[#1877F2] hover:bg-[#166FE5] text-white'}
        data-testid="meta-embedded-signup-btn"
      >
        {connecting ? (
          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Conectando con Meta...</>
        ) : (
          <><MessageCircle className="w-4 h-4 mr-2" /> Conectar WhatsApp con Meta (un clic)</>
        )}
      </Button>
    </div>
  );
}
